/*
 * <custom-product>
 * 商品詳細: アコーディオン開閉 (スマホ) と、サイズ選択時の価格・購入ボタンの更新。
 *
 * 売り切れのサイズを選んだときは、カートボタンを隠して再入荷通知ボタンを出す。
 * coming soon の商品 (data-coming-soon) は、販売開始日時 (data-sales-start、UNIX 秒) までは
 * 在庫があっても入荷通知ボタンだけを出す。ページがキャッシュされていても、開始時刻を過ぎていれば
 * 読み込み時にカートボタンへ切り替える。
 * 再入荷通知ボタンは Back in Stock (AMP) アプリの登録ポップアップを開く
 * (アプリの埋め込みが読み込む window.BIS)。
 */
if (!customElements.get('custom-product')) {
  customElements.define(
    'custom-product',
    class CustomProduct extends HTMLElement {
      connectedCallback() {
        // アコーディオン
        this.querySelectorAll('[data-cpd-acc]').forEach((head) =>
          head.addEventListener('click', () => this.toggleAcc(head))
        );

        // サイズ選択で価格と購入ボタンを更新
        this.priceEl = this.querySelector('[data-cpd-price] .cpd__price-current');
        this.addButton = this.querySelector('[data-cpd-add]');
        this.notifyButton = this.querySelector('[data-cpd-notify]');
        this.querySelectorAll('input[name="id"]').forEach((input) =>
          input.addEventListener('change', () => {
            if (!input.checked) return;
            if (this.priceEl && input.dataset.price) {
              this.priceEl.textContent = input.dataset.price;
            }
            this.updateBuyButton(input.dataset.available === 'true');
          })
        );

        if (this.notifyButton) {
          this.notifyButton.addEventListener('click', () => this.openRestockNotify());
        }

        // キャッシュされたページで、もう販売開始時刻を過ぎている場合
        if (this.hasAttribute('data-coming-soon') && !this.isComingSoon()) {
          this.removeAttribute('data-coming-soon');
          const salesStart = this.querySelector('[data-cpd-sales-start]');
          if (salesStart) salesStart.hidden = true;
          // 空欄にしていたお届け予定を戻す
          const delivery = this.querySelector('[data-cpd-delivery]');
          if (delivery) delivery.classList.remove('cpd__delivery--blank');
          // 売り切れサイズを選んだときに出る文言を「入荷」から「再入荷」に戻す
          if (this.notifyButton) this.notifyButton.textContent = this.notifyButton.dataset.restockLabel;
          const selected = this.querySelector('input[name="id"]:checked');
          if (selected) this.updateBuyButton(selected.dataset.available === 'true');
        }
      }

      // 販売開始前か (data-sales-start は UNIX 秒)。
      // 販売開始日時が未入力の商品は data-sales-start が無く、ずっと coming soon
      isComingSoon() {
        if (!this.hasAttribute('data-coming-soon')) return false;
        if (!this.dataset.salesStart) return true;
        const start = Number(this.dataset.salesStart);
        return start > Date.now() / 1000;
      }

      // 在庫あり: カートボタン / 売り切れ・coming soon: 再入荷通知ボタン
      updateBuyButton(available) {
        if (!this.addButton || !this.notifyButton) return;
        if (this.isComingSoon()) available = false;
        this.addButton.hidden = !available;
        // 隠すだけでなく無効にもして、Enter キーでフォームが送信されないようにする
        this.addButton.disabled = !available;
        this.notifyButton.hidden = available;
      }

      openRestockNotify() {
        const selected = this.querySelector('input[name="id"]:checked');
        const variantId = selected ? Number(selected.value) : null;
        if (window.BIS && window.BIS.popup && variantId) {
          window.BIS.popup.show({ variantId });
          styleRestockPopup();
        } else {
          // アプリの埋め込みが無効、またはスクリプトの読み込み前
          console.warn('Back in Stock (window.BIS) が読み込まれていません');
        }
      }


      toggleAcc(head) {
        const body = head.nextElementSibling;
        const expanded = head.getAttribute('aria-expanded') === 'true';
        head.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (body) body.hidden = expanded;
      }
    }
  );
}

/**
 * Back in Stock (AMP) の登録ポップアップのフォントをサイトに揃え、黒い背景のクリックで閉じるようにする。
 *
 * ポップアップは iframe (#BIS_frame、src は about:blank) の中に描画されるため、
 * テーマの CSS は届かない。about:blank の iframe は親ページと同じオリジンなので、
 * JS で iframe の <head> にフォントの定義とスタイルを差し込む。
 * アプリは読み込み時や登録完了時に iframe の中身を丸ごと書き換えるので、
 * ポップアップが開いているあいだは差し込んだスタイルが消えていないかを見て、消えていれば入れ直す。
 * フォントは scss/_fonts.scss と $font-family-ja (scss/_variables.scss) に合わせている。
 */
const PVZ_BIS_STYLE_ID = 'pvz-bis-font';
const PVZ_BIS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@200..700&display=swap');
@font-face {
  font-family: 'PVZ JP';
  src: local('HiraKakuProN-W3'), local('ヒラギノ角ゴシック W3'), local('HiraginoSans-W3'),
    local('Hiragino Sans'), local('YuGothic-Medium'), local('Yu Gothic Medium'), local('Yu Gothic');
  size-adjust: 94%;
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'PVZ JP';
  src: local('HiraKakuProN-W6'), local('ヒラギノ角ゴシック W6'), local('HiraginoSans-W6'),
    local('YuGothic-Bold'), local('Yu Gothic Bold');
  size-adjust: 94%;
  font-weight: 700;
  font-style: normal;
}
html, body, body *, input, select, textarea, button {
  font-family: 'Oswald', 'PVZ JP', 'Hiragino Kaku Gothic ProN', 'Hiragino Sans', 'Yu Gothic Medium',
    'Yu Gothic', YuGothic, 'Noto Sans JP', sans-serif !important;
  letter-spacing: 0.04em;
}
`;

let pvzBisTimer = null;

function styleRestockPopup() {
  if (pvzBisTimer) return;

  const inject = () => {
    const frame = document.getElementById('BIS_frame');
    if (!frame) return;
    // 閉じたら見張りをやめる (次に開いたときにまた始める)
    if (frame.style.display === 'none' && frame.dataset.pvzShown) {
      clearInterval(pvzBisTimer);
      pvzBisTimer = null;
      delete frame.dataset.pvzShown;
      return;
    }
    let doc;
    try {
      doc = frame.contentDocument;
    } catch (e) {
      return;
    }
    if (!doc || !doc.head || !doc.body || !doc.body.firstChild) return;
    if (frame.style.display !== 'none') frame.dataset.pvzShown = 'true';

    // 黒い背景をクリックしたら閉じる。
    // アプリも「#BISModal の外を押したら閉じる」処理を持っているが、#BISModal が画面全体を覆う
    // .modal を含んでいるため、背景を押しても「中」と判定されて閉じない。
    // ここではダイアログ本体 (.modal-content) の外かどうかで判定する。
    // document は中身を書き換えられても同じものが使われるので、登録は 1 回だけ
    if (!doc.pvzBackdropClose) {
      doc.pvzBackdropClose = true;
      doc.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof frame.contentWindow.Element)) return;
        if (!target.closest('#BISModal')) return; // 閉じるアニメーション中などは何もしない
        if (target.closest('.modal-content')) return;
        if (target.closest('.iti__country-list')) return; // 電話番号の国選択リスト
        if (window.BIS && window.BIS.popup) window.BIS.popup.hide();
      });
    }

    if (doc.getElementById(PVZ_BIS_STYLE_ID)) return;
    const style = doc.createElement('style');
    style.id = PVZ_BIS_STYLE_ID;
    style.textContent = PVZ_BIS_CSS;
    doc.head.appendChild(style);
  };

  inject();
  pvzBisTimer = setInterval(inject, 200);
}

/**
 * サムネイル ⇄ メイン画像スライダーの連動 (Figma: 商品画像)
 * サムネイルをタップするとメインを該当位置までスクロールし、
 * メインをスクロールすると現在のサムネイルに枠を付ける。
 */
document.querySelectorAll('[data-cpd-gallery]').forEach((gallery) => {
  const track = gallery.querySelector('[data-cpd-track]');
  const thumbs = Array.from(gallery.querySelectorAll('[data-cpd-thumb]'));
  if (!track || thumbs.length === 0) return;

  const setCurrent = (index) => {
    thumbs.forEach((t, i) => t.classList.toggle('is-current', i === index));
  };

  // サムネイルをタップしたときは、スムーススクロールが終わるまで
  // スクロール由来の更新を止める。そうしないと通過する途中のサムネイルに
  // 次々と枠が付いてしまう。
  let lockCurrent = false;
  let lockTimer;
  const unlockAfterScrollStops = () => {
    clearTimeout(lockTimer);
    lockTimer = setTimeout(() => {
      lockCurrent = false;
    }, 150);
  };

  thumbs.forEach((thumb, index) => {
    thumb.addEventListener('click', () => {
      setCurrent(index);
      lockCurrent = true;
      // スクロールが起きないケース (既にその位置) でも解除されるようにしておく
      unlockAfterScrollStops();

      const slide = track.children[index];
      if (slide) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    });
  });

  const progressBar = gallery.querySelector('[data-cpd-progress-bar]');
  const updateProgress = () => {
    if (!progressBar) return;
    const max = track.scrollWidth - track.clientWidth;
    const ratio = max > 0 ? track.scrollLeft / max : 0;
    const travel = track.clientWidth - progressBar.offsetWidth;
    progressBar.style.transform = `translateX(${ratio * travel}px)`;
  };
  updateProgress();
  window.addEventListener('resize', updateProgress);

  let ticking = false;
  track.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateProgress();

      // サムネイルからの移動中は枠を動かさない (止まったら解除)
      if (lockCurrent) {
        unlockAfterScrollStops();
        ticking = false;
        return;
      }

      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      Array.from(track.children).forEach((slide, i) => {
        const mid = slide.offsetLeft - track.offsetLeft + slide.clientWidth / 2;
        const best = track.children[closest];
        const bestMid = best.offsetLeft - track.offsetLeft + best.clientWidth / 2;
        if (Math.abs(mid - center) < Math.abs(bestMid - center)) closest = i;
      });
      setCurrent(closest);
      ticking = false;
    });
  });
});
